import React, { useState } from 'react';
import { ArrowRight, Check, Loader2, CalendarCheck, Users, LineChart } from 'lucide-react';
import SEO from '../components/SEO';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';
import { getAttribution, track } from '../lib/analytics';

const EXPECT = [
  { icon: CalendarCheck, title: '30-minute walkthrough', body: 'A live tour of BriefingIQ and EventIQ tailored to your program — no slideware.' },
  { icon: Users, title: 'Your use cases, not ours', body: 'Bring a real briefing or event. We show how intake, scheduling, AI recaps and Salesforce sync would run for it.' },
  { icon: LineChart, title: 'ROI model in hand', body: 'You leave with a pipeline-influence and time-saved estimate you can take to finance.' },
];

const empty = { name: '', email: '', phone: '', company: '', role: '', product: 'both', message: '' };
const inputCls = 'mt-1.5 rounded-lg border-[var(--line-2)]';

const RequestDemoPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState(empty);
  const [website, setWebsite] = useState('');
  const [startedAt] = useState(() => Date.now());

  const change = (f) => (e) => setForm({ ...form, [f]: e.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/contact', { ...form, source: 'demo_request', website, form_started_at: startedAt, attribution: getAttribution() });
      track('demo_request_submit', { label: form.product, product: form.product });
      setDone(true);
      toast({ title: 'Request received', description: `Thanks ${form.name || 'there'} — our team will reach out shortly.` });
    } catch (err) {
      toast({ title: 'Something went wrong', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Request a demo"
        description="See BriefingIQ and EventIQ in action. Book a 30-minute walkthrough tailored to your executive briefing or corporate event program."
        path="/request-demo"
        keywords={['BriefingIQ demo', 'executive briefing software demo', 'event management platform demo']}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Request a demo', path: '/request-demo' }]}
      />

      <section className="bg-white pt-20 pb-12 lg:pt-28 lg:pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.2em] text-ink-3 mb-5">Request a demo</div>
          <h1 className="headline-xl text-ink mb-6">See it in action.</h1>
          <p className="text-lg text-ink-2 max-w-2xl leading-relaxed">
            A 30-minute walkthrough of BriefingIQ and EventIQ, built around your program. We&apos;ll follow up within one business day.
          </p>
        </div>
      </section>

      <section className="bg-[var(--bg-alt)] section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-8">
            <div className="text-xs uppercase tracking-[0.2em] text-ink-3">What to expect</div>
            {EXPECT.map((x) => {
              const Icon = x.icon;
              return (
                <div key={x.title} className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-white border border-[var(--line-2)] flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-ink" />
                  </div>
                  <div>
                    <div className="text-ink font-medium">{x.title}</div>
                    <p className="text-sm text-ink-2 mt-1 leading-relaxed">{x.body}</p>
                  </div>
                </div>
              );
            })}
            <p className="text-sm text-ink-3">Prefer email? Write to <a href="mailto:sales@briefingiq.com" className="text-ink hover:underline">sales@briefingiq.com</a>.</p>
          </div>

          <div className="lg:col-span-3">
            {done ? (
              <div className="bg-white rounded-2xl border border-[var(--line-2)] p-10 text-center" data-testid="demo-success">
                <div className="w-14 h-14 mx-auto rounded-full bg-[var(--bg-alt)] flex items-center justify-center mb-5">
                  <Check className="w-6 h-6 text-ink" strokeWidth={2.5} />
                </div>
                <h3 className="text-2xl font-semibold text-ink mb-2 tracking-tight">You&apos;re all set.</h3>
                <p className="text-sm text-ink-2">A member of our team will reach out within one business day to schedule your demo.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="relative bg-white rounded-2xl border border-[var(--line-2)] p-8 space-y-4" data-testid="demo-form">
                <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden opacity-0 pointer-events-none" aria-hidden="true">
                  <label htmlFor="demo-website">Website</label>
                  <input id="demo-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} data-testid="demo-honeypot" />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="demo-name" className="text-[11px] uppercase tracking-wider text-ink-3">Full name</Label>
                    <Input id="demo-name" required value={form.name} onChange={change('name')} className={inputCls} placeholder="Jane Doe" data-testid="demo-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="demo-email" className="text-[11px] uppercase tracking-wider text-ink-3">Work email</Label>
                    <Input id="demo-email" type="email" required value={form.email} onChange={change('email')} className={inputCls} placeholder="jane@company.com" data-testid="demo-email-input" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="demo-phone" className="text-[11px] uppercase tracking-wider text-ink-3">Phone (optional)</Label>
                    <Input id="demo-phone" type="tel" autoComplete="tel" value={form.phone} onChange={change('phone')} className={inputCls} placeholder="+1 (555) 000-0000" data-testid="demo-phone-input" />
                  </div>
                  <div>
                    <Label htmlFor="demo-company" className="text-[11px] uppercase tracking-wider text-ink-3">Company</Label>
                    <Input id="demo-company" required value={form.company} onChange={change('company')} className={inputCls} placeholder="Acme Corp" data-testid="demo-company-input" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="demo-role" className="text-[11px] uppercase tracking-wider text-ink-3">Role</Label>
                    <Input id="demo-role" value={form.role} onChange={change('role')} className={inputCls} placeholder="Briefing Manager" />
                  </div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-wider text-ink-3">Interested in</Label>
                    <Select value={form.product} onValueChange={change('product')}>
                      <SelectTrigger className={inputCls} data-testid="demo-product-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="briefingiq">BriefingIQ — Executive briefings</SelectItem>
                        <SelectItem value="eventiq">EventIQ — Corporate events</SelectItem>
                        <SelectItem value="both">Both products</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="demo-message" className="text-[11px] uppercase tracking-wider text-ink-3">Notes (optional)</Label>
                  <Textarea id="demo-message" value={form.message} onChange={change('message')} rows={3} className={inputCls} placeholder="Volumes, teams, integrations…" />
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="demo-submit-btn">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request demo <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-[10px] text-ink-3 text-center">By submitting, you agree to our privacy policy.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default RequestDemoPage;
